import React, { useState, useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { TopNavMenuItem } from './TopNavMenuItem';
import { TopNavMenuLink } from './TopNavMenuLink';
import { SiteHeaderContext } from './SiteHeaderContext';

// TODO: Look for a better way to handle the global dom event listener (try forwardRef)

const SubmenuContainer = ({ pres: Comp, ...props }) => {
   const { expandMenuAt } = useContext(SiteHeaderContext);
   const [visible, setVisible] = useState(false);

   const wrapperRef = useRef();
   const clickHandled = useRef();

   useEffect(() => {
      const handleDomClick = ({ target }) => {
         if (
            (!clickHandled.current &&
               wrapperRef &&
               !wrapperRef.current.contains(target)) ||
            (target && target.nodeName === 'A')
         ) {
            setVisible(false);
         }
         clickHandled.current = false;
      };

      document.addEventListener('click', handleDomClick);

      return () => document.removeEventListener('click', handleDomClick);

      // eslint-disable-next-line
   }, []);

   const handleClick = () => {
      setVisible(!visible);
      clickHandled.current = true;
   };

   const handleBlur = (e) => {
      if (
         (window.matchMedia(`(min-width: ${expandMenuAt})`).matches ||
            ![...document.querySelectorAll('[data-rcl-collapsible-menu]')].find(
               (node) => node.contains(e.target)
            )) &&
         !e.currentTarget.contains(e.relatedTarget)
      ) {
         setVisible(false);
      }
   };

   return (
      <Comp
         {...props}
         visible={visible}
         onClick={handleClick}
         onBlur={handleBlur}
         wrapperRef={wrapperRef}
      />
   );
};

const SubmenuHoc = (Comp, props) => <SubmenuContainer pres={Comp} {...props} />;

const SubmenuWrapper = styled.div`
   position: relative;
`;

const SubmenuMenu = styled.ul`
   display: ${({ visible }) => (visible ? 'block' : 'none')};
   position: absolute;
   top: ${({ theme }) => theme.sizes.siteHeaderHeight};
   ${({ horizontalAlign }) => horizontalAlign} : 0;
   list-style: none;
   margin: 0;
   padding: 0;
   min-width: 180px;
   background-color: ${({ theme }) => theme.brandColors.white};
   box-shadow: -1px 5px 5px 0 hsla(222, 34%, 7%, 0.07);
   line-height: 2.5rem;
   white-space: normal;

   && ${TopNavMenuItem} {
      margin: 0;
      min-width: 100%;
      line-height: 2.5rem;
   }

   ${TopNavMenuLink} {
      min-width: 100%;
      font-family: SharpSans, sans-serif;
      color: ${({ theme }) => theme.brandColors.darkGray};
      border: none;
      text-transform: none;
      line-height: 2.5rem;

      :hover,
      :focus {
         background-color: ${({ theme }) => theme.brandColors.extraLightGray};
      }

      &::after {
         top: 17px;
         right: auto;
      }
   }

   & & {
      padding-left: 1em;
   }
`;

const SubmenuPresentation = ({
   title = '',
   component: Comp = TopNavMenuLink,
   horizontalAlign = 'left',
   visible = false,
   onClick,
   onBlur = undefined,
   children,
   wrapperRef,
}) => (
   <SubmenuWrapper onBlur={onBlur} ref={wrapperRef}>
      <Comp hasSubmenu submenuOpen={visible} as="button" onClick={onClick}>
         {title}
      </Comp>
      <SubmenuMenu visible={visible} horizontalAlign={horizontalAlign}>
         {children}
      </SubmenuMenu>
   </SubmenuWrapper>
);

SubmenuPresentation.propTypes = {
   title: PropTypes.string,
   horizontalAlign: PropTypes.oneOf(['right', 'left']),
   visible: PropTypes.bool,
   onClick: PropTypes.func.isRequired,
   onBlur: PropTypes.func,
   children: PropTypes.node.isRequired,
};

const TopNavSubmenu = (props) => SubmenuHoc(SubmenuPresentation, props);

export { TopNavSubmenu, SubmenuMenu };
